<template>
  <main class="u-container text-center pb-10">
    <h2>Welcome Back</h2>
    <p class="mt-2">
      One moment while we process your Change-of-Address order.
    </p>
    <AppLoader class="mt-6" />
  </main>
</template>

<script>
import { validate } from 'uuid';
import Cookies from 'js-cookie';
import { isDesktop } from '../utils/deviceType';
import AppLoader from '../components/AppLoader.vue';

export default {
  name: 'Action',
  components: {
    AppLoader,
  },
  data() {
    return {
      coa: {},
      dcoa: false,
      mover: {},
      oldAddress: {},
      forwardType: '',
      moverType: '',
    };
  },
  computed: {
    flagAmpersendEnabled() {
      return this.$store.get('flagAmpersendEnabled');
    },
    coaUUIDUrl() {
      return this.$router?.params?.orderUuid;
    },
  },
  async mounted() {
    // Route params need to be destructured and set on $coa prior to API calls
    const uuid = new URL(window.location)?.pathname?.split('/')?.pop();
    const {
      cuid,
      ord: orderNumber,
      coa_uuid: coaId,
      utm_campaign: campaign,
      utm_medium: medium,
      utm_source: source,
    } = this.$route.query;

    const parsedUUID = coaId.split(/\?/).filter((item) => validate(item))?.pop();
    const coaUUID = validate(uuid) ? uuid : parsedUUID;

    this.$store.set('instantSavings/orderUuid', coaUUID);
    if (!coaUUID) {
      this.$router.replace({ name: 'processing-error' });
      return;
    }
    this.$coa.coaUUID = coaUUID;
    if (this.coaUUIDUrl) {
      sessionStorage.setItem('coaUUID', this.coaUUIDUrl);
    }
    await this.hydrateCOA();
    await this.hydrateAddresses();

    const destination = {
      name: 'confirm-order',
      params: { orderUuid: coaUUID },
      query: {
        dcoa: this.dcoa,
        utm_campaign: campaign || 'default',
        utm_medium: medium || 'sp',
        utm_source: source || 'usps',
      },
    };

    if (this.oldAddress.military) {
      await this.hydrateMover();
    }
    const hasMultipleBusiness = this.coa.multipleAddresses;
    const cfpsId = await this.$coa.coaCommit({
      coaUUID, cuid, orderNumber, hasMultipleBusiness,
    });
    const isCommitted = typeof cfpsId === 'string' || typeof cfpsId === 'number' || cfpsId === null;
    Cookies.set('coa_complete', cfpsId, { sameSite: 'strict' });

    if (!isCommitted) {
      Object.assign(destination, {
        name: 'processing-error',
        query: {},
      });
    }

    Object.assign(destination.query, { cfpsId });

    if (isCommitted && this.flagAmpersendEnabled && this.oldAddress.military !== true) {
      this.$store.set('instantSavings/token', this.$coa.token);
      this.$store.set('instantSavings/orderUuid', this.$coa.coaUUID);
      await this.$nextTick();

      const anonymousId = await this.$store.dispatch(
        'instantSavings/identify',
        {
          informedDelivery: this.coa.informedDelivery,
          deviceType: isDesktop() ? 'DESKTOP' : 'MOBILE',
        },
      );

      this.$amp.setAnonymousID(anonymousId);
      this.$amp.initSDK();

      if (this.$store.get('config').liveOffersEnabled) {
        await this.$store.dispatch('instantSavings/getOffers', {
          schemas: [
            'MGO Advantage Single',
            'MGO Advantage Double',
            'MGO Advantage Print Default',
            'MGO Advantage Print Comcast',
          ],
          viewAt: this.$route.query.viewAt ? new Date(this.$route.query.viewAt).toISOString() : '',
        });
      }
    }

    if (isCommitted && this.$store.get('instantSavings/offers').length) {
      Object.assign(destination, {
        name: 'instant-savings',
      });
    }

    if (this.oldAddress.military) {
      Object.assign(destination.query, {
        mcoa: true,
      });
    }

    this.$router.replace(destination);
  },
  methods: {
    async hydrateCOA() {
      const { uuid, ...coa } = await this.$coa.coaGet() || [];

      if (uuid) {
        this.coa = coa;
        this.dcoa = coa.dcoa;
        this.forwardType = coa.forwardType;
        this.moverType = coa.moverType;
      }
    },
    async hydrateMover() {
      const mover = await this.$coa.personGet('mover');
      if (mover) {
        this.mover = mover;
      }
    },
    async hydrateAddresses() {
      const { addresses } = await this.$coa.addressGetAll();
      const [address1, address2, address3] = addresses;
      [
        address1,
        address2,
        address3,
      ]
        .filter((a) => a)
        .forEach(({ addressType, ...address }) => {
          if (addressType === 'BILLING') {
            return;
          }

          this[`${addressType.toLowerCase()}Address`] = { addressType, ...address };
        });
    },
  },
};
</script>
